/**
 * Graphics choices the player makes in the settings menu and the renderer obeys, kept on this device. Presentation only: none of it reaches the server.
 *
 * `spellLights` is how many spell orbs may light the world at once. Each is a real light, and every lit surface pays for every light in the scene
 * whether it is lit or not (and every lit shader the loading screen links unrolls them), so this is the dial for weaker graphics cards. `4` is the
 * default; `'all'` gives every hero seat and enemy caster an orb light of its own, 20 and more on the coast; `0` turns the lights off and leaves
 * only the orbs' glow. Changing it changes the number of lights, which relinks every lit shader once, behind the loading overlay
 * (`GameScene.followLighting`).
 *
 * `torchLights` is the same dial for heroes' torches (`entities/torches.ts`), `4` by default: the torches nearest the view light the world, the
 * rest burn with flame and glow only.
 *
 * `shadows` turns the sun's shadows on or off (every lit surface relinks once, the same way). `corpseShadows` lets the fallen keep casting theirs: a figure
 * is drawn again into the shadow map for each of its surfaces, so a field of corpses costs nearly as much as the living.
 *
 * `pictureStyle` puts the game's picture style (pixel grid, ink, paper grain and tone; `picture-filters.ts`) over the world. On unless saved off.
 *
 * `characterDetail` is how finely figures in the world are meshed: `high` (the default; a saved `medium`, retired, loads as it) and `low` simplify
 * heads, hair and beards, which the playing zoom shows a few dozen pixels across. Close-ups (the inventory, the character creator, the lobby) always
 * draw `high`. Changing it rebuilds every figure behind the loading overlay.
 *
 * `grass` is how much of the continuous grass and crops the world draws (`environment/grass-field.ts`): `full` (the default), `thin` (half
 * the blades) or `off` (the painted ground alone). It changes how many blades draw, with nothing rebuilt.
 *
 * `overheadText` is how much floating text the world shows over units: `off` (none), `effects` (ability and condition captions such as
 * SNEAK ATTACK, but no numbers) or `all` (the default: captions and damage, healing and miss numbers). It filters the display only.
 */
export type SpellLights = 'all' | 8 | 4 | 2 | 1 | 0;
export const SPELL_LIGHT_CHOICES: readonly SpellLights[] = ['all', 8, 4, 2, 1, 0];
export type TorchLights = SpellLights;
export const TORCH_LIGHT_CHOICES: readonly TorchLights[] = SPELL_LIGHT_CHOICES;
export type CharacterDetail = 'high' | 'low';
export const CHARACTER_DETAIL_CHOICES: readonly CharacterDetail[] = ['high', 'low'];
export type GrassSetting = 'full' | 'thin' | 'off';
export const GRASS_CHOICES: readonly GrassSetting[] = ['full', 'thin', 'off'];
export type OverheadTextLevel = 'off' | 'effects' | 'all';
export const OVERHEAD_TEXT_CHOICES: readonly OverheadTextLevel[] = ['off', 'effects', 'all'];
const KEY = 'nimblez.graphics';

function parse(value: unknown, fallback: SpellLights = 'all'): SpellLights { return SPELL_LIGHT_CHOICES.find(choice => choice === value) ?? fallback; }
/** On unless saved off. */
const flag = (value: unknown) => value !== false;

export class GraphicsOptions {
  private lights: SpellLights = 4;
  private torches: TorchLights = 4;
  private shadowsOn = true;
  private corpsesCast = true;
  private detail: CharacterDetail = 'high';
  private styled = true;
  private overhead: OverheadTextLevel = 'all';
  private grassCover: GrassSetting = 'full';
  private listeners = new Set<() => void>();
  constructor() {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) ?? '{}');
      this.lights = parse(saved.spellLights, 4); this.torches = parse(saved.torchLights, 4); this.shadowsOn = flag(saved.shadows); this.corpsesCast = flag(saved.corpseShadows); this.styled = flag(saved.pictureStyle);
      this.detail = CHARACTER_DETAIL_CHOICES.find(choice => choice === saved.characterDetail) ?? 'high';
      this.overhead = OVERHEAD_TEXT_CHOICES.find(choice => choice === saved.overheadText) ?? 'all';
      this.grassCover = GRASS_CHOICES.find(choice => choice === saved.grass) ?? 'full';
    } catch { /* Storage may be unavailable: the defaults stand. */ }
  }

  get spellLights(): SpellLights { return this.lights; }
  /** The most orbs that may light the world at once: a number, `Infinity` for every caster's. */
  get spellLightLimit(): number { return this.lights === 'all' ? Infinity : this.lights; }
  get torchLights(): TorchLights { return this.torches; }
  get torchLightLimit(): number { return this.torches === 'all' ? Infinity : this.torches; }
  get shadows() { return this.shadowsOn; }
  get corpseShadows() { return this.corpsesCast; }
  get characterDetail() { return this.detail; }
  get pictureStyle() { return this.styled; }
  get overheadText() { return this.overhead; }
  get grass() { return this.grassCover; }
  setSpellLights(value: unknown) {
    const next = parse(value, 4);
    if (next === this.lights) return;
    this.lights = next; this.changed();
  }
  setTorchLights(value: unknown) {
    const next = parse(value, this.torches);
    if (next === this.torches) return;
    this.torches = next; this.changed();
  }
  setShadows(on: boolean) { if (on !== this.shadowsOn) { this.shadowsOn = on; this.changed(); } }
  setPictureStyle(on: boolean) { if (on !== this.styled) { this.styled = on; this.changed(); } }
  setCorpseShadows(on: boolean) { if (on !== this.corpsesCast) { this.corpsesCast = on; this.changed(); } }
  setCharacterDetail(value: unknown) {
    const next = CHARACTER_DETAIL_CHOICES.find(choice => choice === value);
    if (next && next !== this.detail) { this.detail = next; this.changed(); }
  }
  setOverheadText(value: unknown) {
    const next = OVERHEAD_TEXT_CHOICES.find(choice => choice === value);
    if (next && next !== this.overhead) { this.overhead = next; this.changed(); }
  }
  setGrass(value: unknown) {
    const next = GRASS_CHOICES.find(choice => choice === value);
    if (next && next !== this.grassCover) { this.grassCover = next; this.changed(); }
  }
  /** Calls `listener` whenever a choice changes; the returned function stops it. */
  onChange(listener: () => void) { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }
  private changed() {
    try { localStorage.setItem(KEY, JSON.stringify({ spellLights: this.lights, torchLights: this.torches, shadows: this.shadowsOn, corpseShadows: this.corpsesCast, characterDetail: this.detail, pictureStyle: this.styled, overheadText: this.overhead, grass: this.grassCover })); } catch { /* The choice lasts for this visit only. */ }
    for (const listener of [...this.listeners]) listener();
  }
}

export const graphicsOptions = new GraphicsOptions();
